import {
  BreadcrumbProps,
  Card,
  Colors,
  ResizeEntry,
  ResizeSensor,
} from "@blueprintjs/core";
import { useSize } from "ahooks";
import classNames from "classnames";
import { Loading } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import styled from "styled-components";

import { ERoutes } from "../AppRouter";
import { useBreadcrumbs } from "../contexts";
import { AppError } from "./AppError";

export interface ICustomBreadcrumbProps extends Omit<BreadcrumbProps, "href"> {
  route?: string | ERoutes;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  align-self: stretch;
  padding: 0.5em;

  &.overflow {
    overflow: auto;
  }
`;

const FixedStickyContainer = styled.div<{
  containerTopPosition: number;
  containerLeftPosition: number;
}>`
  position: fixed;
  top: ${(props) => props.containerTopPosition}px;
  left: ${(props) => props.containerLeftPosition}px;
  right: 0;
  background-color: ${Colors.WHITE};
  z-index: 15;
`;

const Wrapper: React.FC<{ withCard?: boolean }> = ({ withCard, children }) =>
  withCard ? (
    <Card elevation={1} style={{ padding: "0.5em", minHeight: "100%" }}>
      {children}
    </Card>
  ) : (
    <>{children}</>
  );

interface IPageBase {
  breadCrumbs?: ICustomBreadcrumbProps[];
  isLoading?: boolean;
  withCard?: boolean;
  stickyContent?: () => React.ReactNode;
  allowOverflow?: boolean;
}

export const PageBase: React.FunctionComponent<IPageBase> = ({
  breadCrumbs,
  children,
  isLoading = false,
  withCard = true,
  stickyContent = null,
  allowOverflow = true,
}) => {
  const { setBaseBreadcrumb, setDetailsBreadcrumb } = useBreadcrumbs();
  const history = useHistory();
  React.useEffect(() => {
    setDetailsBreadcrumb([]);
  }, [setDetailsBreadcrumb]);

  React.useEffect(() => {
    if (breadCrumbs) {
      const nextBr: BreadcrumbProps[] = breadCrumbs.map((br) =>
        br.route
          ? {
              ...br,
              onClick: () => {
                if (br.route) {
                  history.push(br.route);
                }
              },
            }
          : { ...br }
      );
      setBaseBreadcrumb(nextBr);
    }
  }, [breadCrumbs, history, setBaseBreadcrumb]);

  const fixedStickyContainerRef = React.useRef<HTMLDivElement>(null);
  const fixedStickyContainerDimensions = useSize(fixedStickyContainerRef);

  const [stickyTopPosition, setStickyTopPosition] = React.useState(0);
  const [stickyLeftPosition, setStickyLeftPosition] = React.useState(0);

  const onRootResize = React.useCallback(
    (entries: ResizeEntry[]) => {
      entries.forEach((e) => {
        const rootContainerClientRect = e.target.getBoundingClientRect();
        if (rootContainerClientRect.top !== stickyTopPosition)
          setStickyTopPosition(rootContainerClientRect.top);
        if (rootContainerClientRect.left !== stickyLeftPosition)
          setStickyLeftPosition(rootContainerClientRect.left);
      });
    },
    [stickyLeftPosition, stickyTopPosition]
  );

  return (
    <ResizeSensor onResize={onRootResize}>
      <Container className={classNames({ overflow: allowOverflow })}>
        <div
          style={{
            paddingTop: fixedStickyContainerDimensions?.height,
            flex: 1,
            flexDirection: "column",
          }}
        >
          {stickyContent && (
            <FixedStickyContainer
              ref={fixedStickyContainerRef}
              containerLeftPosition={stickyLeftPosition}
              containerTopPosition={stickyTopPosition}
            >
              {stickyContent()}
            </FixedStickyContainer>
          )}
          <Wrapper withCard={withCard}>
            <AppError>
              <Loading isLoading={isLoading}>{children}</Loading>
            </AppError>
          </Wrapper>
        </div>
      </Container>
    </ResizeSensor>
  );
};
